import { Check, Search, UserPlus } from 'lucide-react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import type { SharePickerClient } from './share-care-plan-modal.types';

type ExistingClientPanelProps = {
  search: string;
  onSearchChange: (next: string) => void;
  isLoadingClients: boolean;
  clients: SharePickerClient[];
  selectedClientId: string | null;
  onSelectClient: (id: string | null) => void;
};

/**
 * Recipient picker for clients that already have a bQuest relationship:
 * a search box over a selectable list. Each row animates its selected
 * checkmark in, and the whole panel is swapped with a slide/fade by its
 * parent tab container.
 */
export const ExistingClientPanel = ({
  search,
  onSearchChange,
  isLoadingClients,
  clients,
  selectedClientId,
  onSelectClient,
}: ExistingClientPanelProps) => (
  <div className="space-y-3">
    <div className="relative">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <input
        type="search"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search clients by name or email"
        className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm transition-colors focus:border-sage focus:outline-none focus:ring-1 focus:ring-sage"
        aria-label="Search clients"
      />
    </div>

    {isLoadingClients ? (
      <Skeleton className="h-32 w-full" />
    ) : clients.length === 0 ? (
      <p className="text-sm text-gray-500 px-1">
        No matching clients. Try the &ldquo;Invite a new client&rdquo; tab.
      </p>
    ) : (
      <ul className="max-h-64 overflow-y-auto rounded-md border border-gray-200">
        {clients.map((c) => {
          const isSelected = selectedClientId === c.id;
          return (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onSelectClient(c.id)}
                className={`flex w-full items-center gap-3 border-b border-gray-100 px-3 py-2 text-left transition-colors duration-150 hover:bg-gray-50 ${
                  isSelected ? 'bg-sage-bg' : ''
                }`}
              >
                <span
                  className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border transition-all duration-200 ${
                    isSelected
                      ? 'scale-100 border-sage bg-sage text-white'
                      : 'scale-90 border-gray-300 text-transparent'
                  }`}
                >
                  <Check className="h-3 w-3" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {c.name}
                  </p>
                  <p className="truncate text-xs text-gray-600">{c.email}</p>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

type NewClientPanelProps = {
  newClientName: string;
  onNewClientNameChange: (next: string) => void;
  newClientEmail: string;
  onNewClientEmailChange: (next: string) => void;
};

/**
 * Off-platform recipient form: name (optional) + email. Used on the
 * "Invite a new client" tab.
 */
export const NewClientPanel = ({
  newClientName,
  onNewClientNameChange,
  newClientEmail,
  onNewClientEmailChange,
}: NewClientPanelProps) => (
  <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-4">
    <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
      <UserPlus className="h-4 w-4 text-sage" aria-hidden />
      Invite a new client
    </div>
    <div className="space-y-2">
      <label htmlFor="share-new-client-name" className="text-xs font-medium text-gray-700">
        Client name (optional)
      </label>
      <input
        id="share-new-client-name"
        type="text"
        value={newClientName}
        maxLength={200}
        onChange={(e) => onNewClientNameChange(e.target.value)}
        placeholder="Jane Doe"
        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm transition-colors focus:border-sage focus:outline-none focus:ring-1 focus:ring-sage"
      />
    </div>
    <div className="space-y-2">
      <label htmlFor="share-new-client-email" className="text-xs font-medium text-gray-700">
        Client email
      </label>
      <input
        id="share-new-client-email"
        type="email"
        value={newClientEmail}
        maxLength={200}
        onChange={(e) => onNewClientEmailChange(e.target.value)}
        placeholder="jane@example.com"
        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm transition-colors focus:border-sage focus:outline-none focus:ring-1 focus:ring-sage"
      />
    </div>
    <p className="text-xs text-gray-500">
      When you send, your client will get a bQuest invitation alongside the plan.
    </p>
  </div>
);
